import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaTruck, FaCalendarAlt, FaMapMarkerAlt, FaWeightHanging, 
  FaEye, FaBuilding, FaUserTie, FaCheckCircle, 
  FaClock, FaExclamationTriangle, FaFilter,
  FaSearch, FaSync, FaUsers, FaDollarSign,
  FaChevronDown, FaBox
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';

function ActiveJobs({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [user, setUser] = useState({});
  const [subbies, setSubbies] = useState([]);
  const [filters, setFilters] = useState({
    subbieId: '',
    jobNumber: '',
    status: '',
    dateRange: 'all',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const subbieRes = await fetch(`${API_URL}/subcontractors`);
      const subbieData = await subbieRes.json();
      if (subbieData.success) {
        setSubbies(subbieData.subcontractors || []);
      }

      const jobsRes = await fetch(`${API_URL}/fleet/admin/active-jobs`);
      const jobsData = await jobsRes.json();
      if (jobsData.success) {
        setJobs(jobsData.jobs || []);
        setFilteredJobs(jobsData.jobs || []);
      } else {
        setError(jobsData.error || 'Failed to fetch active jobs');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'quoting': 'bg-blue-100 text-blue-700',
      'approved': 'bg-green-100 text-green-700',
      'accepted': 'bg-green-100 text-green-700',
      'assigned': 'bg-purple-100 text-purple-700',
      'in_progress': 'bg-indigo-100 text-indigo-700',
      'completed': 'bg-gray-100 text-gray-700',
      'closed': 'bg-gray-100 text-gray-700'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': '⏳ Pending',
      'quoting': '📝 Quoting',
      'approved': '✅ Approved',
      'accepted': '✅ Accepted',
      'assigned': '🚛 Assigned',
      'in_progress': '🔄 In Progress',
      'completed': '✅ Completed',
      'closed': '🔒 Closed'
    };
    return labels[status] || status;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      'low': 'bg-gray-100 text-gray-600',
      'normal': 'bg-blue-100 text-blue-600',
      'high': 'bg-orange-100 text-orange-600',
      'urgent': 'bg-red-100 text-red-600'
    };
    return `px-2 py-0.5 rounded-full text-xs font-semibold ${styles[priority?.toLowerCase()] || 'bg-gray-100 text-gray-600'}`;
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    applyFilters(newFilters);
  };

  const applyFilters = (currentFilters = filters) => {
    let filtered = [...jobs];

    if (currentFilters.subbieId) {
      filtered = filtered.filter(j => j.subcontractor_id === currentFilters.subbieId);
    }

    if (currentFilters.jobNumber) {
      filtered = filtered.filter(j => 
        j.job?.job_number?.toLowerCase().includes(currentFilters.jobNumber.toLowerCase())
      );
    }

    if (currentFilters.status) {
      filtered = filtered.filter(j => j.status === currentFilters.status);
    }

    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];

    if (currentFilters.dateRange === 'today') {
      filtered = filtered.filter(j => j.job?.start_date === todayStr);
    } else if (currentFilters.dateRange === 'last7') {
      filtered = filtered.filter(j => {
        const date = j.job?.start_date;
        return date >= sevenDaysAgoStr && date <= todayStr;
      });
    } else if (currentFilters.dateRange === 'custom') {
      if (currentFilters.startDate) {
        filtered = filtered.filter(j => j.job?.start_date >= currentFilters.startDate);
      }
      if (currentFilters.endDate) {
        filtered = filtered.filter(j => j.job?.start_date <= currentFilters.endDate);
      }
    }

    setFilteredJobs(filtered);
  };

  const clearFilters = () => {
    setFilters({
      subbieId: '',
      jobNumber: '',
      status: '',
      dateRange: 'all',
      startDate: '',
      endDate: ''
    });
    setFilteredJobs(jobs);
  };

  if (loading) {
    return (
      <AdminLayout activeTab="activejobs" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading active jobs...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout activeTab="activejobs" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Jobs</h2>
          <p className="text-gray-500">{error}</p>
          <button 
            onClick={() => fetchData()} 
            className="mt-4 px-4 py-2 rounded-lg text-white" 
            style={{ backgroundColor: '#00A86B' }}
          >
            Retry
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="activejobs" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">📋 All Active Jobs</h1>
          <p className="text-sm text-gray-500">
            {filteredJobs.length} active jobs across all subcontractors
          </p>
        </div>
        <button
          onClick={() => fetchData()}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors"
        >
          <FaSync size={14} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-4 mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-400" />
            <span className="text-sm font-medium text-gray-600">Filters:</span>
          </div>

          <select
            value={filters.subbieId}
            onChange={(e) => handleFilterChange('subbieId', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green min-w-[150px]"
            style={{ borderColor: '#E8ECEF' }}
          >
            <option value="">All Subbies</option>
            {subbies.map(s => (
              <option key={s.id} value={s.id}>{s.company_name}</option>
            ))}
          </select>

          <input
            type="text"
            value={filters.jobNumber}
            onChange={(e) => handleFilterChange('jobNumber', e.target.value)}
            placeholder="Search Job #"
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green w-40"
            style={{ borderColor: '#E8ECEF' }}
          />

          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green"
            style={{ borderColor: '#E8ECEF' }}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="quoting">Quoting</option>
            <option value="approved">Approved</option>
            <option value="accepted">Accepted</option>
            <option value="assigned">Assigned</option>
            <option value="in_progress">In Progress</option>
          </select>

          <select
            value={filters.dateRange}
            onChange={(e) => handleFilterChange('dateRange', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green"
            style={{ borderColor: '#E8ECEF' }}
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="last7">Last 7 Days</option>
            <option value="custom">Custom Range</option>
          </select>

          {filters.dateRange === 'custom' && (
            <>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
              />
            </>
          )}

          <button
            onClick={clearFilters}
            className="text-sm text-red-500 hover:text-red-700"
          >
            Clear All
          </button>
        </div>
      </div>

      {filteredJobs.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-xl font-semibold text-navy">No Active Jobs Found</h3>
          <p className="text-sm text-gray-500">
            No active jobs match your current filters. Try adjusting your filters.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredJobs.map((req) => {
            const job = req.job || {};
            const subbie = req.subcontractor || {};
            const totalTrucks = req.trucks_assigned || 0;
            const requiredTrucks = req.trucks_required || 0;
            const totalMT = job.total_quantity_mt || 0;
            
            let deliveredMT = 0;
            if (req.allocations) {
              req.allocations.forEach(unit => {
                if ((unit.status === 'delivered' || unit.status === 'completed') && unit.pod_weight_mt) {
                  deliveredMT += parseFloat(unit.pod_weight_mt) || 0;
                }
              });
            }
            
            const progress = totalMT > 0 ? Math.round((deliveredMT / totalMT) * 100) : 0;

            const jobType = job.quote_type || 'minerals';
            const typeBar = {
              'minerals': 'border-l-red-400',
              'agri': 'border-l-green-400',
              'abnormal': 'border-l-purple-400',
              'container': 'border-l-yellow-400',
              'general': 'border-l-blue-400'
            }[jobType] || 'border-l-gray-200';
            const typeBadge = {
              'minerals': 'bg-red-100 text-red-700',
              'agri': 'bg-green-100 text-green-700',
              'abnormal': 'bg-purple-100 text-purple-700',
              'container': 'bg-yellow-100 text-yellow-700',
              'general': 'bg-blue-100 text-blue-700'
            }[jobType] || 'bg-gray-100 text-gray-600';

            return (
              <div key={req.id} className={`bg-white rounded-xl border border-l-4 ${typeBar} p-4 shadow-sm hover:shadow-md transition-shadow`} style={{ borderColor: '#E8ECEF' }}>
                <div className="flex flex-wrap justify-between items-start gap-4">
                  {/* Left - Job Info */}
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-navy">{job.job_number || 'Unknown Job'}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${typeBadge}`}>
                        {jobType === 'container' ? '📦 Containers' : jobType === 'abnormal' ? '🚛 Abnormal' : jobType === 'agri' ? '🌾 Agri' : jobType === 'minerals' ? '⛏️ Minerals' : '📋 General'}
                      </span>
                      <span className={getStatusBadge(req.status)}>{getStatusLabel(req.status)}</span>
                      {req.priority && (
                        <span className={getPriorityBadge(req.priority)}>
                          {req.priority.toUpperCase()}
                        </span>
                      )}
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        {totalTrucks}/{requiredTrucks} trucks
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">
                      <FaUserTie className="inline mr-1 text-green-500" /> 
                      {subbie.company_name || 'Unknown Subbie'}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <FaBuilding className="inline mr-1 text-blue-500" /> 
                      {job.client?.company_name || 'Unknown Client'}
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
                      {jobType === 'container' ? (
                        <span className="flex items-center gap-1 font-medium text-navy">
                          <FaBox size={14} className="text-yellow-500" />
                          {job.container_count || 1} × {job.container_size || '6m'} {job.container_type || 'dry'}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <FaWeightHanging size={14} className="text-green-500" /> 
                          {totalMT} MT
                        </span>
                      )}
                      {job.cross_border === true && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">🛂 Cross-Border</span>
                      )}
                      <span className="flex items-center gap-1">
                        <FaMapMarkerAlt size={14} className="text-blue-500" /> 
                        {job.origin || 'Unknown'} → {job.destination || 'Unknown'}
                      </span>
                      {job.rate_per_mt && (
                        <span className="flex items-center gap-1 font-medium text-green-600">
                          <FaDollarSign size={14} /> R{job.rate_per_mt}/MT
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <FaCalendarAlt size={14} className="text-gray-400" /> 
                        {job.start_date || 'TBD'}
                      </span>
                    </div>
                  </div>

                  {/* Right - Actions */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => toast.info('Job details view coming soon')}
                      className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm hover:border-green hover:text-green transition-colors"
                      style={{ borderColor: '#E8ECEF', color: '#4A5568' }}
                    >
                      <FaEye size={14} /> View
                    </button>
                    
                    {/* ✅ SCHEDULE ADMIN BUTTON - PLACEHOLDER */}
                    {user.role === 'admin' && (
                      <button
                        onClick={() => toast.info('⏳ Schedule Admin feature coming soon!')}
                        style={{
                          backgroundColor: '#9CA3AF',
                          color: 'white',
                          fontWeight: '600',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          opacity: 0.6
                        }}
                      >
                        <FaTruck size={14} /> Schedule (Admin) ⌛
                      </button>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                {totalMT > 0 && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-medium text-gray-600">Delivery Progress</span>
                      <span className="text-xs font-bold text-green-600">{progress}%</span>
                    </div>
                    <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${progress}%`, backgroundColor: '#00A86B' }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{deliveredMT.toFixed(1)} MT delivered</span>
                      <span>{totalMT} MT total</span>
                    </div>
                  </div>
                )}

                {/* Quick Status of Allocated Units */}
                {req.allocations && req.allocations.length > 0 && (
                  <div className="mt-3 pt-3 border-t" style={{ borderColor: '#E8ECEF' }}>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <FaTruck className="text-green-500" /> 
                        {req.allocations.length} truck{req.allocations.length !== 1 ? 's' : ''} allocated
                      </span>
                      <span className="flex items-center gap-1">
                        <FaUsers className="text-blue-500" />
                        {req.allocations.filter(a => a.driver_id).length} drivers assigned
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {req.allocations.slice(0, 8).map((unit, idx) => {
                        const statusColors = {
                          'pending': 'bg-gray-200',
                          'on_route_to_loading': 'bg-indigo-200',
                          'onsite': 'bg-blue-200',
                          'loading': 'bg-yellow-200',
                          'en_route': 'bg-purple-200',
                          'at_destination': 'bg-orange-200',
                          'delivered': 'bg-green-200',
                          'completed': 'bg-green-300'
                        };
                        return (
                          <span 
                            key={idx} 
                            className={`px-2 py-0.5 rounded text-xs ${statusColors[unit.status] || 'bg-gray-200'} text-gray-700`}
                            title={`Unit ${idx + 1}: ${unit.status}`}
                          >
                            {unit.truck?.registration_number || `U${idx + 1}`}
                          </span>
                        );
                      })}
                      {req.allocations.length > 8 && (
                        <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500">
                          +{req.allocations.length - 8} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}

export default ActiveJobs;
